import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Heart, ThumbsUp, X } from "lucide-react";
import type { ViewerPlaylist } from "@demore/shared/types";
import { ViewerShell } from "./ViewerShell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Rating = "very_interested" | "interested" | "not_interested";

export function RatePage() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [ratings, setRatings] = useState<Record<string, Rating>>({});
  const { data, isLoading } = useQuery({
    queryKey: ["viewer-playlist", token],
    queryFn: () => api.get<ViewerPlaylist>(`/api/view/${token}/rate`),
    enabled: !!token,
  });

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/view/${token}/rate`, {
        ratings: Object.entries(ratings).map(([videoRef, rating]) => ({ videoRef, rating })),
      }),
    onSuccess: () => {
      // Bug 12: the watch page reads the same ["viewer-playlist", token] query.
      // Without dropping the cache it renders the PRE-rating order, so the
      // adaptive reorder never appears (especially with partial ratings).
      qc.removeQueries({ queryKey: ["viewer-playlist", token] });
      nav(`/view/${token}/watch`);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save"),
  });

  if (isLoading) return <ViewerShell><div className="text-center py-20">Loading…</div></ViewerShell>;
  if (!data) return null;

  function pick(videoRef: string, rating: Rating) {
    setRatings((r) => ({ ...r, [videoRef]: rating }));
  }

  return (
    <ViewerShell>
      <div className="space-y-6">
        <header className="text-center space-y-1">
          <h1 className="font-head text-2xl md:text-3xl font-semibold">Tell us what interests you</h1>
          <p className="text-navy-200 text-sm">We'll reorder the playlist to put what you care about first.</p>
        </header>
        <ul className="space-y-3">
          {data.items.filter((i) => i.playable).map((it) => (
            <li key={it.videoRef} className="bg-viewer-soft rounded-md p-3 sm:p-4 flex items-start gap-3 sm:gap-4">
              <div className="w-20 sm:w-24 aspect-video bg-navy rounded shrink-0 overflow-hidden">
                {it.thumbnailUrl && <img src={it.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{it.title}</div>
                <p className="text-xs text-navy-200 mt-0.5 line-clamp-2">{it.description}</p>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <RatingButton active={ratings[it.videoRef] === "very_interested"} onClick={() => pick(it.videoRef, "very_interested")} icon={<Heart className="h-3.5 w-3.5" />} label="Very interested" />
                  <RatingButton active={ratings[it.videoRef] === "interested"} onClick={() => pick(it.videoRef, "interested")} icon={<ThumbsUp className="h-3.5 w-3.5" />} label="Interested" />
                  <RatingButton active={ratings[it.videoRef] === "not_interested"} onClick={() => pick(it.videoRef, "not_interested")} icon={<X className="h-3.5 w-3.5" />} label="Not interested" />
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-col-reverse sm:flex-row gap-2 justify-end pt-2">
          <Button variant="ghost" onClick={() => nav(`/view/${token}/watch`)}>Skip — use original order</Button>
          <Button size="lg" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Continue to videos"}
          </Button>
        </div>
      </div>
    </ViewerShell>
  );
}

function RatingButton({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors",
        active ? "bg-gold text-navy border-gold" : "border-navy-300 text-navy-100 hover:border-gold/60",
      )}
    >
      {icon}
      {label}
    </button>
  );
}
